import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/reducers';

import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { roleMapping, User } from '../types/user.types';
import { RemoveUser, UpdateUser } from 'src/app/state/actions/settings.actions';
import { Observable, catchError, finalize, lastValueFrom, map, of, shareReplay, tap } from 'rxjs';
import { PUBLICLOGIN } from 'src/app/authentication-lead/authentication-lead.module';
import { OriginalUrlService } from './original-url.service';
import { UtilsService } from './utils.service';

@Injectable()
export class AuthenticationService {
  private static readonly MATCH_MODAL_SEEN_KEY = 'match_modal_seen';

  constructor(
    private http: HttpClient,
    private originalUrlService: OriginalUrlService,
    private router: Router,
    private store: Store<AppState>,
    private utilsService: UtilsService
  ) {}

  loginUrl = environment.apiUrl + '/auth/jwt/create/';
  private refreshInFlight$?: Observable<string | null>;

  public signUp(newUser) {
    return this.http.post<HttpResponse<any>>(environment.apiUrl + '/auth/users/', { ...newUser });
  }

  public validateUser(uid, token) {
    return this.http.post<HttpResponse<any>>(environment.apiUrl + '/users/request_user_activation/', { uid, token });
  }

  /**
   * Lets the user log with standard credentials to BE
   * @param username login credential to project's BE
   * @param password login credential to project's BE
   * @returns True if user logged in successfuly or False otherwise
   */
  public async logIn(username: string, password: string): Promise<User | null> {
    try {
      const logInResult = await lastValueFrom(
        this.http.post<{ access: string; refresh: string; previous_last_login?: string | null }>(this.loginUrl, {
          username,
          password,
        })
      );
      if (logInResult && logInResult.access) {
        this.storePendingPreviousLastLogin(logInResult?.previous_last_login ?? null);
        saveTokens(logInResult);
        const user = await this.getMe();
        this.commitPendingPreviousLastLogin(user?.id);
        this.store.dispatch(new UpdateUser(user));
        return user;
      } else {
        return null;
      }
    } catch (err) {
      return null;
    }
  }

  public getToken(): string {
    return localStorage.getItem('access_token');
  }

  private getJwtPayload(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  public isAccessTokenExpired(leewaySeconds = 30): boolean {
    const token = this.getToken();
    if (!token) return true;

    const payload = this.getJwtPayload(token);
    const exp = payload?.exp;
    if (typeof exp !== 'number') return true;

    const now = Math.floor(Date.now() / 1000);
    return exp <= now + leewaySeconds;
  }

  /**
   * Refresh access token (shared across concurrent callers).
   * Returns `null` when refresh isn't possible (missing/invalid refresh token).
   */
  public refreshToken(): Observable<string | null> {
    if (this.refreshInFlight$) return this.refreshInFlight$;

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return of(null);

    this.refreshInFlight$ = this.http.post<{ access?: string }>(`${environment.apiUrl}/auth/jwt/refresh/`, { refresh: refreshToken }).pipe(
      map(res => res?.access ?? null),
      tap(access => {
        if (access) {
          saveTokens({ access, refresh: refreshToken });
        }
      }),
      catchError(err => {
        // Refresh failed (expired refresh / revoked / malformed). Log out to prevent token_not_valid spam.
        this.signOut();
        this.router.navigate([PUBLICLOGIN]);
        return of(null);
      }),
      finalize(() => {
        this.refreshInFlight$ = undefined;
      }),
      shareReplay(1)
    );

    return this.refreshInFlight$;
  }

  /**
   * Removes user's traces
   */
  signOut(): void {
    this.originalUrlService.setOriginalUrl(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('user_role');
    localStorage.removeItem('unreadConv');
    this.store.dispatch(new RemoveUser());
    this.utilsService.clearStore();

    history.pushState(null, '', location.href);
    window.onpopstate = function () {
      history.go(1);
    };
    setTimeout(() => this.router.navigate([PUBLICLOGIN]));
  }

  /**
   * Fetches the currently authenticated user from the backend using the access token in local storage.
   *
   * If the token is invalid or not present, the user is logged out, and null is returned. The function
   * also resolves the user's role based on specific logic, including handling `dlRegister` overrides.
   *
   * @param dlRegister - If request is from deep_link registration, the user's `last_used_role` is directly used for the `role` field.
   * @returns A `User` object if the token is valid, or `null` if the token is invalid or not present.
   */
  public async getMe(dlRegister = false): Promise<User> {
    if (localStorage.getItem('access_token') === null) {
      return null;
    }
    try {
      const user = await lastValueFrom(this.getUser());

      // Resolve the user's role based on logic and mappings
      let role: string =
        // First, it attempts to fetch the user's role from `localStorage`
        localStorage.getItem('user_role') ||
        // Then if the user's last used role is not 'AD', that role is used. Otherwise, it maps the user's roles (excluding role with value `1`== AD) to a corresponding code from the `roleMapping` object.
        (user.last_used_role !== 'AD' ? user.last_used_role : roleMapping[user.roles.find(role => role !== 1)]?.code);
      // Override role if `dlRegister` is true
      if (dlRegister) {
        role = user.last_used_role;
      }
      // Construct the internal user data object
      const user_data: User = {
        username: user.username,
        id: user.id,
        email: user.email,
        roles: user.roles,
        role: role,
        first_name: user.first_name,
        last_name: user.last_name,
      };
      return { ...user_data };
    } catch (err) {
      this.signOut();
      this.router.navigate([PUBLICLOGIN]);
      return null;
    }
  }

  public getUser(): Observable<User> {
    return this.http.get<User>(environment.apiUrl + '/auth/users/me/').pipe(
      catchError(error => {
        throw error; // Rethrow the error so it can be caught downstream
      })
    );
  }

  public updateUser(updatedData): Observable<User> {
    return this.http.patch<User>(environment.apiUrl + '/auth/users/me/', { ...updatedData });
  }

  public resetPassword(email: string) {
    return this.http.post<HttpResponse<any>>(environment.apiUrl + '/auth/users/reset_password/', { email });
  }

  public resetPasswordConfirm(uid: string, token: string, new_password: string) {
    return this.http.post<HttpResponse<any>>(
      environment.apiUrl + '/auth/users/reset_password_confirm/',
      { uid, token, new_password },
      { observe: 'response' }
    );
  }

  public resetPasswordTokenCheck(uid, token) {
    return this.http.post<HttpResponse<any>>(environment.apiUrl + '/auth/users/reset_token_check', {
      uid,
      token,
    });
  }

  public setPassword(current_password, new_password) {
    return this.http.post<HttpResponse<any>>(environment.apiUrl + '/auth/users/set_password/', {
      current_password,
      new_password,
    });
  }

  public async socialLogin(provider: string, code: string, signup: boolean, deeplinkId?: string | null) {
    try {
      const tokenResult = await lastValueFrom(
        this.http.post<{ access: string; refresh: string; previous_last_login?: string | null }>(
          environment.apiUrl + '/auth/jwt/create/' + provider + '/',
          {
            code,
            signup,
            ...(deeplinkId ? { deeplink: deeplinkId } : {}),
          }
        )
      );
      if (tokenResult && tokenResult.access) {
        this.storePendingPreviousLastLogin(tokenResult?.previous_last_login ?? null);
        saveTokens(tokenResult);
        const user = await this.getMe();
        this.commitPendingPreviousLastLogin(user?.id);
        this.store.dispatch(new UpdateUser(user));
        return user;
      }
    } catch (error) {
      throw error;
    }
  }

  private storePendingPreviousLastLogin(previousLastLogin: string | null): void {
    const root = this.readMatchModalSeenRoot();
    root['_meta'] = this.isPlainObject(root['_meta']) ? root['_meta'] : {};
    root['_meta'].pendingPreviousLastLogin = previousLastLogin;
    this.writeMatchModalSeenRoot(root);
  }

  private commitPendingPreviousLastLogin(userId?: string): void {
    if (!userId) return;

    const root = this.readMatchModalSeenRoot();
    const pending = root['_meta']?.pendingPreviousLastLogin;
    if (pending === undefined) return;

    const uid = String(userId);
    const bucket = this.normalizeUserMatchModalBucket(root[uid]);
    bucket.previous_last_login = pending;
    root[uid] = bucket;

    if (!this.isPlainObject(root['_meta'])) root['_meta'] = {};
    delete root['_meta'].pendingPreviousLastLogin;
    if (Object.keys(root['_meta']).length === 0) delete root['_meta'];

    this.writeMatchModalSeenRoot(root);
  }

  private readMatchModalSeenRoot(): Record<string, any> {
    try {
      const raw = localStorage.getItem(AuthenticationService.MATCH_MODAL_SEEN_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return this.isPlainObject(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeMatchModalSeenRoot(root: Record<string, any>): void {
    try {
      localStorage.setItem(AuthenticationService.MATCH_MODAL_SEEN_KEY, JSON.stringify(root));
    } catch {
      // best-effort only
    }
  }

  private normalizeUserMatchModalBucket(existing: unknown): { seen: Record<string, string>; previous_last_login?: string | null } {
    // v3 bucket
    if (this.isPlainObject(existing) && 'seen' in existing) {
      const ex: any = existing;
      return {
        seen: this.isPlainObject(ex.seen) ? (ex.seen as Record<string, string>) : {},
        previous_last_login: ex.previous_last_login ?? null,
      };
    }

    // v2 per-user flat map (relationId -> matched_date)
    if (this.isPlainObject(existing)) {
      return { seen: existing as Record<string, string> };
    }

    return { seen: {} };
  }

  private isPlainObject(value: unknown): value is Record<string, any> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}

function saveTokens(tokens: { access: string; refresh: string }) {
  // Keep tokenExpiration in sync with access token `exp` (seconds since epoch).
  try {
    const parts = tokens.access.split('.');
    if (parts.length === 3) {
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const parsed = JSON.parse(atob(padded));
      const exp = parsed?.exp;
      if (typeof exp === 'number') {
        localStorage.setItem('tokenExpiration', new Date(exp * 1000).toString());
      }
    }
  } catch {
    // If parsing fails, keep existing tokenExpiration (if any).
  }
  localStorage.setItem('access_token', tokens.access);
  localStorage.setItem('refresh_token', tokens.refresh);
}
